import type { ResourceKey } from 'i18next'
import resourcesToBackend from 'i18next-resources-to-backend'
import sentry from 'utils/sentry/sentry'
import { createTransifexLoader } from './customBackend'

type Translations = Record<string, string>
type ResourcesCallback = (error: Error | null, resources: ResourceKey | null) => void

const OVERRIDE_FETCH_TIMEOUT_MS = 10_000

const TRANSLATION_FILES: Record<LanguageCode, () => Promise<{ default: Translations }>> = {
  en: () => import('./translations/en.json'),
  sv: () => import('./translations/sv.json'),
  nb: () => import('./translations/nb.json'),
  fi: () => import('./translations/fi.json'),
  de: () => import('./translations/de.json'),
}

function hasTranslationFile(language: string): language is LanguageCode {
  return Object.hasOwn(TRANSLATION_FILES, language)
}

async function loadTranslationFile(language: string): Promise<Translations> {
  if (!hasTranslationFile(language)) throw new Error(`No translation file for ${language}`)
  return (await TRANSLATION_FILES[language]()).default
}

type TranslationLoaderOptions = {
  loadStandard: (language: string) => Promise<Translations>
  loadOverride?: (language: string) => Promise<Translations>
  reportOverrideFailure: (error: unknown, language: string) => void
}

/**
 * Builds a loader that returns a language's standard translations with the Organization's override for
 * that language applied on top.
 */
export function createTranslationLoader({
  loadStandard,
  loadOverride,
  reportOverrideFailure,
}: TranslationLoaderOptions): (language: string) => Promise<Translations> {
  return async (language) => {
    const override = loadOverride?.(language).catch((error: unknown) => {
      reportOverrideFailure(error, language)
      return {}
    })
    const standard = await loadStandard(language)
    return { ...standard, ...(await override) }
  }
}

function reportOverrideFailureToSentry(error: unknown, language: string): void {
  sentry.captureException(error, {
    level: 'warning',
    tags: { sentry_scope: 'i18n', issue_type: 'organization_override_load_failed', language },
    fingerprint: ['i18n', 'organization_override_load_failed'],
  })
}

/**
 * Builds the i18next backend that serves the translation files shipped with the app, with the Organization's
 * Transifex override applied on top when the system has one. An override that fails or does not answer within
 * the timeout leaves the files alone.
 */
export function createFilesBackend(overrideToken: string | undefined): ReturnType<typeof resourcesToBackend> {
  const loadTranslations = createTranslationLoader({
    loadStandard: loadTranslationFile,
    loadOverride: overrideToken ? createTransifexLoader(overrideToken, OVERRIDE_FETCH_TIMEOUT_MS) : undefined,
    reportOverrideFailure: reportOverrideFailureToSentry,
  })
  // The three-argument form, because with a returned promise the library unwraps a `default` key, which an
  // override may contain.
  return resourcesToBackend((language: string, _namespace: string, callback: ResourcesCallback) => {
    loadTranslations(language).then(
      (translations) => callback(null, translations),
      (error: Error) => callback(error, null)
    )
  })
}
